import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnalyticsContext } from './AnalyticsContext';
import {
  generatePageloadId,
  getAnalyticsWithPageloadId,
  getUtmParameters,
} from '../../analytics/analytics-utils';
import { UtmParameters } from '../../appTypings';
import { Analytics } from '@yleisradio/areena-types';
import { sendPageloadAnalytics } from '../../analytics/sendPageloadAnalytics';
import { useAppFeaturesContext } from '../AppFeaturesContext/AppFeaturesContext';
import { useAuthContext } from '../AuthContext/AuthContext';
import { getTracker } from '../../analytics/snowplow';
import { logger } from '../../../common/logger';
import {
  trackPageView,
  addGlobalContexts,
  setUserId,
} from '@snowplow/browser-tracker';
export const AnalyticsContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { version, device, applicationResolution, mode } =
    useAppFeaturesContext();
  const { user } = useAuthContext();
  const [pageloadId, setPageloadId] = useState<string>('');
  const refererRef = useRef<string | undefined>();
  const utmRef = useRef<UtmParameters>({});

  const sendPageloadAnalyticsCb = useCallback(
    (analytics: Analytics) => {
      const pageloadId = generatePageloadId();

      sendPageloadAnalytics({
        analytics: getAnalyticsWithPageloadId(analytics, pageloadId),
        applicationResolution,
        device,
        loggedInUser: user,
        updateReferer: referer => (refererRef.current = referer),
        clearUtmParameters: () => (utmRef.current = {}),
        version,
        referer: refererRef.current,
        utmParameters: utmRef.current,
      });

      setPageloadId(pageloadId);
    },
    [applicationResolution, device, user, version]
  );

  useEffect(() => {
    const { isSnowplowRunning } = getTracker(mode);
    if (isSnowplowRunning) {
      trackPageView();
      logger.debug('Snowplow is running');
    }
  }, [mode]);

  useEffect(() => {
    const { isSnowplowRunning } = getTracker(mode);
    if (isSnowplowRunning && user?.consent) {
      const version = user.consent.version;
      addGlobalContexts({
        yle_consent: {
          schema: 'iglu:fi.yle/consent/jsonschema/2-0-1',
          data: {
            version: `v${version.toString()}`,
            development: user?.consent?.values.development,
            personalization: user?.consent?.values.personalization,
            embedded_social_media: user?.consent?.values.embedded_social_media,
            targeting: user?.consent?.values.targeting,
          },
        },
      });
      if (user?.transientId) {
        setUserId(user?.transientId);

        if (user?.isSubAccount) {
          addGlobalContexts({
            user_profile: {
              schema: 'iglu:fi.yle.content/user_profile/jsonschema/2-0-0',
              data: {
                age_group: user.age,
                predicate: 'consumer',
              },
            },
          });
        }
      }
    }
  }, [user?.consent, user?.age, user?.transientId, user?.isSubAccount, mode]);

  return (
    <AnalyticsContext.Provider
      value={{
        pageloadId,
        setUtmParameters: (mode, isPlayer, id) => {
          utmRef.current = getUtmParameters(mode, isPlayer, id);
        },
        sendPageloadAnalytics: sendPageloadAnalyticsCb,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
